import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { units } from '../data/units';
import { getConceptProgress, getUnitProgress, resetProgress } from '../data/progress';
import styled from 'styled-components';

interface WeakConcept {
  unitId: string;
  unitTitle: string;
  unitColor: string;
  conceptId: string;
  conceptTitle: string;
  bestScore: number;
  lessonDone: boolean;
  hasAttempted: boolean;
}

const RING_SIZE = 140;
const RING_STROKE = 10;
const RING_RADIUS = (RING_SIZE - RING_STROKE) / 2;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

function getScoreColor(score: number, attempted: boolean): string {
  if (!attempted) return '#9e9e9e';
  if (score >= 80) return '#4caf50';
  if (score >= 50) return '#ff9800';
  return '#f44336';
}

const ProgressScreen: React.FC = () => {
  const navigate = useNavigate();
  const [expandedUnits, setExpandedUnits] = useState<Record<string, boolean>>({});
  const [showResetConfirm, setShowResetConfirm] = useState(false);

  // Compute overall stats
  let totalConcepts = 0;
  let totalCompleted = 0;
  let totalScoreSum = 0;
  let totalScoredConcepts = 0;

  const unitData = units.map((unit) => {
    const conceptIds = unit.concepts.map((c) => c.id);
    const progress = getUnitProgress(unit.id, conceptIds);
    totalConcepts += progress.total;
    totalCompleted += progress.completed;

    const conceptDetails = unit.concepts.map((concept) => {
      const cp = getConceptProgress(unit.id, concept.id);
      if (cp.bestQuizScore > 0) {
        totalScoreSum += cp.bestQuizScore;
        totalScoredConcepts++;
      }
      return { concept, progress: cp };
    });

    return { unit, progress, conceptDetails };
  });

  const overallPct = totalConcepts > 0 ? Math.round((totalCompleted / totalConcepts) * 100) : 0;
  const overallAvgScore = totalScoredConcepts > 0 ? Math.round(totalScoreSum / totalScoredConcepts) : 0;
  const ringOffset = RING_CIRCUMFERENCE - (overallPct / 100) * RING_CIRCUMFERENCE;

  // Weak areas: score < 70% OR lesson done but never attempted quiz
  const weakAreas: WeakConcept[] = [];
  unitData.forEach(({ unit, conceptDetails }) => {
    conceptDetails.forEach(({ concept, progress: cp }) => {
      const hasAttempted = cp.quizScores.length > 0;
      const isWeak =
        (hasAttempted && cp.bestQuizScore < 70) ||
        (cp.lessonCompleted && !hasAttempted);
      if (isWeak) {
        weakAreas.push({
          unitId: unit.id,
          unitTitle: unit.title,
          unitColor: unit.color,
          conceptId: concept.id,
          conceptTitle: concept.title,
          bestScore: cp.bestQuizScore,
          lessonDone: cp.lessonCompleted,
          hasAttempted,
        });
      }
    });
  });

  const toggleUnit = (unitId: string) => {
    setExpandedUnits((prev) => ({ ...prev, [unitId]: !prev[unitId] }));
  };

  const handleReset = () => {
    resetProgress();
    setShowResetConfirm(false);
    window.location.reload();
  };

  return (
    <PageContainer>
      <BackButton onClick={() => navigate('/')}>
        <BackArrow>&larr;</BackArrow>
        All Units
      </BackButton>

      <PageTitle>Your Progress</PageTitle>

      {/* ─── Overall Stats ─────────────────────────────────────── */}
      <OverallStatsCard>
        <RingSection>
          <RingWrapper>
            <svg width={RING_SIZE} height={RING_SIZE}>
              <circle
                cx={RING_SIZE / 2}
                cy={RING_SIZE / 2}
                r={RING_RADIUS}
                fill="none"
                stroke="#e8eaed"
                strokeWidth={RING_STROKE}
              />
              <circle
                cx={RING_SIZE / 2}
                cy={RING_SIZE / 2}
                r={RING_RADIUS}
                fill="none"
                stroke="#1a73e8"
                strokeWidth={RING_STROKE}
                strokeDasharray={RING_CIRCUMFERENCE}
                strokeDashoffset={ringOffset}
                strokeLinecap="round"
                transform={`rotate(-90 ${RING_SIZE / 2} ${RING_SIZE / 2})`}
                style={{ transition: 'stroke-dashoffset 0.6s ease' }}
              />
            </svg>
            <RingLabel>
              <RingPercent>{overallPct}%</RingPercent>
              <RingCaption>complete</RingCaption>
            </RingLabel>
          </RingWrapper>
        </RingSection>

        <StatsDetails>
          <StatRow>
            <StatIcon>&#128218;</StatIcon>
            <StatText>
              <strong>{totalCompleted}</strong> of <strong>{totalConcepts}</strong> concepts completed
            </StatText>
          </StatRow>
          <StatRow>
            <StatIcon>&#9997;</StatIcon>
            <StatText>
              Overall average quiz score:{' '}
              {overallAvgScore > 0 ? (
                <StatScoreBadge $color={getScoreColor(overallAvgScore, true)}>
                  {overallAvgScore}%
                </StatScoreBadge>
              ) : (
                <StatMuted>No quizzes taken</StatMuted>
              )}
            </StatText>
          </StatRow>
          <StatRow>
            <StatIcon>&#127942;</StatIcon>
            <StatText>
              {totalScoredConcepts} quiz{totalScoredConcepts !== 1 ? 'zes' : ''} attempted
            </StatText>
          </StatRow>
        </StatsDetails>
      </OverallStatsCard>

      {/* ─── Unit-by-Unit Breakdown ────────────────────────────── */}
      <SectionTitle>Unit Breakdown</SectionTitle>

      <UnitList>
        {unitData.map(({ unit, progress, conceptDetails }) => {
          const unitPct = progress.total > 0
            ? Math.round((progress.completed / progress.total) * 100)
            : 0;
          const isExpanded = !!expandedUnits[unit.id];

          return (
            <UnitCard key={unit.id}>
              <UnitCardHeader onClick={() => toggleUnit(unit.id)}>
                <UnitHeaderLeft>
                  <UnitIconDisplay>{unit.icon}</UnitIconDisplay>
                  <UnitHeaderInfo>
                    <UnitNumber $color={unit.color}>Unit {unit.number}</UnitNumber>
                    <UnitTitle>{unit.title}</UnitTitle>
                  </UnitHeaderInfo>
                </UnitHeaderLeft>
                <UnitHeaderRight>
                  <UnitStats>
                    <UnitStatPill>
                      {progress.completed}/{progress.total}
                    </UnitStatPill>
                    {progress.avgScore > 0 && (
                      <UnitScorePill $color={getScoreColor(progress.avgScore, true)}>
                        {progress.avgScore}%
                      </UnitScorePill>
                    )}
                  </UnitStats>
                  <ExpandChevron $expanded={isExpanded}>&#9660;</ExpandChevron>
                </UnitHeaderRight>
              </UnitCardHeader>

              <UnitProgressBarTrack>
                <UnitProgressBarFill $pct={unitPct} $color={unit.color} />
              </UnitProgressBarTrack>

              <ExpandableSection $expanded={isExpanded}>
                <ConceptTable>
                  {conceptDetails.map(({ concept, progress: cp }) => {
                    const hasAttempted = cp.quizScores.length > 0;
                    const scoreColor = getScoreColor(cp.bestQuizScore, hasAttempted);

                    return (
                      <ConceptRow key={concept.id}>
                        <ConceptStatusIcon $completed={cp.lessonCompleted}>
                          {cp.lessonCompleted ? '\u2713' : '\u2014'}
                        </ConceptStatusIcon>
                        <ConceptName>{concept.title}</ConceptName>
                        <ConceptLessonStatus $done={cp.lessonCompleted}>
                          {cp.lessonCompleted ? 'Completed' : 'Not started'}
                        </ConceptLessonStatus>
                        <ConceptQuizScore $color={scoreColor}>
                          {hasAttempted ? `${cp.bestQuizScore}%` : 'Not attempted'}
                        </ConceptQuizScore>
                      </ConceptRow>
                    );
                  })}
                </ConceptTable>
              </ExpandableSection>
            </UnitCard>
          );
        })}
      </UnitList>

      {/* ─── Weak Areas ────────────────────────────────────────── */}
      {weakAreas.length > 0 && (
        <>
          <SectionTitle>Review These</SectionTitle>
          <WeakAreaDescription>
            Concepts where your quiz score is below 70%, or where you completed the lesson but haven't taken the quiz yet.
          </WeakAreaDescription>
          <WeakAreaList>
            {weakAreas.map((item) => (
              <WeakAreaCard key={`${item.unitId}:${item.conceptId}`}>
                <WeakAreaLeft>
                  <WeakAreaConceptTitle>{item.conceptTitle}</WeakAreaConceptTitle>
                  <WeakAreaUnitLabel $color={item.unitColor}>
                    {item.unitTitle}
                  </WeakAreaUnitLabel>
                </WeakAreaLeft>
                <WeakAreaRight>
                  <WeakAreaScore
                    $color={getScoreColor(item.bestScore, item.hasAttempted)}
                  >
                    {item.hasAttempted ? `${item.bestScore}%` : 'No quiz'}
                  </WeakAreaScore>
                  <RetryButton
                    onClick={() =>
                      navigate(`/unit/${item.unitId}/concept/${item.conceptId}/quiz`)
                    }
                  >
                    {item.hasAttempted ? 'Retry Quiz' : 'Take Quiz'}
                  </RetryButton>
                </WeakAreaRight>
              </WeakAreaCard>
            ))}
          </WeakAreaList>
        </>
      )}

      {/* ─── Reset Button ──────────────────────────────────────── */}
      <ResetSection>
        {!showResetConfirm ? (
          <ResetButton onClick={() => setShowResetConfirm(true)}>
            Reset All Progress
          </ResetButton>
        ) : (
          <ResetConfirmBox>
            <ResetConfirmText>
              Are you sure? This will permanently delete all your lesson progress and quiz scores.
            </ResetConfirmText>
            <ResetConfirmActions>
              <ResetConfirmButton $variant="danger" onClick={handleReset}>
                Yes, Reset Everything
              </ResetConfirmButton>
              <ResetConfirmButton $variant="cancel" onClick={() => setShowResetConfirm(false)}>
                Cancel
              </ResetConfirmButton>
            </ResetConfirmActions>
          </ResetConfirmBox>
        )}
      </ResetSection>
    </PageContainer>
  );
};

export default ProgressScreen;

/* ─── Styled Components ─────────────────────────────────────────────────────── */

const PageContainer = styled.div`
  max-width: 860px;
  margin: 0 auto;
  padding: 24px 20px 64px;
  background: #ffffff;
  min-height: 100vh;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen,
    Ubuntu, Cantarell, sans-serif;
`;

const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: none;
  border: none;
  color: #5f6368;
  font-size: 15px;
  cursor: pointer;
  padding: 8px 12px;
  border-radius: 8px;
  margin-bottom: 8px;
  transition: background 0.15s, color 0.15s;

  &:hover {
    background: #f1f3f4;
    color: #202124;
  }
`;

const BackArrow = styled.span`
  font-size: 18px;
  line-height: 1;
`;

const PageTitle = styled.h1`
  font-size: 32px;
  font-weight: 800;
  color: #202124;
  margin: 0 0 28px;
  line-height: 1.2;
  letter-spacing: -0.3px;
`;

/* ─── Overall Stats ────────────────────────────────────────────────────────── */

const OverallStatsCard = styled.div`
  display: flex;
  align-items: center;
  gap: 32px;
  background: linear-gradient(135deg, #f0f4ff 0%, #e8f0fe 100%);
  border: 1px solid #d2e3fc;
  border-radius: 16px;
  padding: 32px;
  margin-bottom: 40px;

  @media (max-width: 600px) {
    flex-direction: column;
    text-align: center;
    padding: 24px 20px;
    gap: 24px;
  }
`;

const RingSection = styled.div`
  flex-shrink: 0;
`;

const RingWrapper = styled.div`
  position: relative;
  width: ${RING_SIZE}px;
  height: ${RING_SIZE}px;
`;

const RingLabel = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
`;

const RingPercent = styled.div`
  font-size: 28px;
  font-weight: 800;
  color: #1a73e8;
  line-height: 1.1;
`;

const RingCaption = styled.div`
  font-size: 12px;
  font-weight: 500;
  color: #5f6368;
  margin-top: 2px;
`;

const StatsDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  flex: 1;
`;

const StatRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 15px;
  color: #3c4043;
  line-height: 1.4;
`;

const StatIcon = styled.span`
  font-size: 16px;
  line-height: 1;
  flex-shrink: 0;
  width: 24px;
  text-align: center;
`;

const StatText = styled.span`
  strong {
    font-weight: 700;
    color: #202124;
  }
`;

const StatScoreBadge = styled.span<{ $color: string }>`
  font-weight: 700;
  color: ${({ $color }) => $color};
`;

const StatMuted = styled.span`
  color: #9e9e9e;
  font-style: italic;
`;

/* ─── Section Title ────────────────────────────────────────────────────────── */

const SectionTitle = styled.h2`
  font-size: 22px;
  font-weight: 700;
  color: #202124;
  margin: 0 0 18px;
  line-height: 1.3;
`;

/* ─── Unit Breakdown ───────────────────────────────────────────────────────── */

const UnitList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  margin-bottom: 40px;
`;

const UnitCard = styled.div`
  background: #ffffff;
  border: 1px solid #e8eaed;
  border-radius: 14px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  transition: box-shadow 0.2s;

  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  }
`;

const UnitCardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 20px 14px;
  cursor: pointer;
  user-select: none;
  gap: 12px;

  &:hover {
    background: #fafbfc;
  }
`;

const UnitHeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  min-width: 0;
`;

const UnitIconDisplay = styled.span`
  font-size: 32px;
  line-height: 1;
  flex-shrink: 0;
`;

const UnitHeaderInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const UnitNumber = styled.span<{ $color: string }>`
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  color: ${({ $color }) => $color};
`;

const UnitTitle = styled.h3`
  font-size: 16px;
  font-weight: 700;
  color: #202124;
  margin: 0;
  line-height: 1.25;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const UnitHeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
`;

const UnitStats = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  @media (max-width: 480px) {
    flex-direction: column;
    gap: 4px;
  }
`;

const UnitStatPill = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: #5f6368;
  background: #f1f3f4;
  padding: 4px 10px;
  border-radius: 10px;
  white-space: nowrap;
`;

const UnitScorePill = styled.span<{ $color: string }>`
  font-size: 13px;
  font-weight: 700;
  color: ${({ $color }) => $color};
  background: ${({ $color }) => $color}15;
  padding: 4px 10px;
  border-radius: 10px;
  white-space: nowrap;
`;

const ExpandChevron = styled.span<{ $expanded: boolean }>`
  font-size: 10px;
  color: #9e9e9e;
  transition: transform 0.25s ease;
  transform: rotate(${({ $expanded }) => ($expanded ? '180deg' : '0deg')});
  line-height: 1;
`;

const UnitProgressBarTrack = styled.div`
  height: 4px;
  background: #eef0f5;
  margin: 0 20px;
`;

const UnitProgressBarFill = styled.div<{ $pct: number; $color: string }>`
  height: 100%;
  width: ${({ $pct }) => $pct}%;
  background: ${({ $color }) => $color};
  border-radius: 2px;
  transition: width 0.4s ease;
`;

/* ─── Expandable Concept Section ───────────────────────────────────────────── */

const ExpandableSection = styled.div<{ $expanded: boolean }>`
  max-height: ${({ $expanded }) => ($expanded ? '1200px' : '0')};
  opacity: ${({ $expanded }) => ($expanded ? 1 : 0)};
  overflow: hidden;
  transition: max-height 0.35s ease, opacity 0.25s ease;
`;

const ConceptTable = styled.div`
  padding: 8px 20px 16px;
  display: flex;
  flex-direction: column;
  gap: 0;
`;

const ConceptRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 0;
  border-bottom: 1px solid #f1f3f4;

  &:last-child {
    border-bottom: none;
  }

  @media (max-width: 600px) {
    flex-wrap: wrap;
    gap: 6px;
  }
`;

const ConceptStatusIcon = styled.span<{ $completed: boolean }>`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 700;
  flex-shrink: 0;
  background: ${({ $completed }) => ($completed ? '#e6f4ea' : '#f1f3f4')};
  color: ${({ $completed }) => ($completed ? '#4caf50' : '#9e9e9e')};
`;

const ConceptName = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: #202124;
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ConceptLessonStatus = styled.span<{ $done: boolean }>`
  font-size: 12px;
  font-weight: 500;
  color: ${({ $done }) => ($done ? '#4caf50' : '#9e9e9e')};
  white-space: nowrap;
  flex-shrink: 0;
  min-width: 80px;
  text-align: right;
`;

const ConceptQuizScore = styled.span<{ $color: string }>`
  font-size: 12px;
  font-weight: 700;
  color: #ffffff;
  background: ${({ $color }) => $color};
  padding: 3px 10px;
  border-radius: 10px;
  white-space: nowrap;
  flex-shrink: 0;
  min-width: 85px;
  text-align: center;
`;

/* ─── Weak Areas ───────────────────────────────────────────────────────────── */

const WeakAreaDescription = styled.p`
  font-size: 14px;
  color: #5f6368;
  margin: -10px 0 16px;
  line-height: 1.5;
`;

const WeakAreaList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 48px;
`;

const WeakAreaCard = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 14px 18px;
  background: #fff8f0;
  border: 1px solid #ffe0b2;
  border-radius: 12px;
  transition: box-shadow 0.2s;

  &:hover {
    box-shadow: 0 2px 8px rgba(255, 152, 0, 0.12);
  }

  @media (max-width: 600px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }
`;

const WeakAreaLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

const WeakAreaConceptTitle = styled.span`
  font-size: 15px;
  font-weight: 600;
  color: #202124;
  line-height: 1.3;
`;

const WeakAreaUnitLabel = styled.span<{ $color: string }>`
  font-size: 12px;
  font-weight: 600;
  color: ${({ $color }) => $color};
`;

const WeakAreaRight = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
`;

const WeakAreaScore = styled.span<{ $color: string }>`
  font-size: 13px;
  font-weight: 700;
  color: ${({ $color }) => $color};
  min-width: 56px;
  text-align: center;
`;

const RetryButton = styled.button`
  font-size: 13px;
  font-weight: 600;
  color: #ffffff;
  background: #ff9800;
  border: none;
  border-radius: 8px;
  padding: 7px 16px;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.15s, transform 0.1s;

  &:hover {
    background: #f57c00;
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
`;

/* ─── Reset Section ────────────────────────────────────────────────────────── */

const ResetSection = styled.div`
  display: flex;
  justify-content: center;
  padding-top: 16px;
  border-top: 1px solid #e8eaed;
`;

const ResetButton = styled.button`
  font-size: 14px;
  font-weight: 600;
  color: #d93025;
  background: none;
  border: 1.5px solid #d93025;
  border-radius: 8px;
  padding: 10px 28px;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;

  &:hover {
    background: #d93025;
    color: #ffffff;
  }
`;

const ResetConfirmBox = styled.div`
  text-align: center;
  background: #fce8e6;
  border: 1px solid #f5c6cb;
  border-radius: 12px;
  padding: 24px 28px;
  max-width: 440px;
  width: 100%;
`;

const ResetConfirmText = styled.p`
  font-size: 14px;
  color: #5f2120;
  margin: 0 0 18px;
  line-height: 1.5;
`;

const ResetConfirmActions = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
  flex-wrap: wrap;
`;

const ResetConfirmButton = styled.button<{ $variant: 'danger' | 'cancel' }>`
  font-size: 14px;
  font-weight: 600;
  border: none;
  border-radius: 8px;
  padding: 10px 22px;
  cursor: pointer;
  transition: background 0.15s, transform 0.1s;

  background: ${({ $variant }) => ($variant === 'danger' ? '#d93025' : '#ffffff')};
  color: ${({ $variant }) => ($variant === 'danger' ? '#ffffff' : '#5f6368')};
  border: ${({ $variant }) => ($variant === 'cancel' ? '1px solid #dadce0' : 'none')};

  &:hover {
    background: ${({ $variant }) => ($variant === 'danger' ? '#b71c1c' : '#f1f3f4')};
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
`;
